import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import Button from "@mui/material/Button";
import TextfieldInput from "@_components/TextfieldInput/TextfieldInput";
import { caseActions } from "@_actions/case.actions";
import styles from "./ContactInfoPopup.module.css";
import {
  catchError,
  isComponentDisabled,
  isNull,
  validateEmail,
} from "@_utils/helpers";
import Genders from "@_configs/Genders.config";
import { MenuItem } from "@mui/material";
import CountryAutocompleteList from "@_configs/CountryAutocompleteList.config";
import { useSnackbar } from "@_hooks/useSnackbar";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";

const componetTag = "contact_info_popup";
function ContactInfoPopup(props) {
  const { opponentData, data } = props;
  const inputDisabled = isComponentDisabled(componetTag, data.state);
  const dispatch = useDispatch();

  const [isClicked, setIsClicked] = useState(false);
  const [inputs, setInputs] = useState({});
  const { snackbarProps, showSnackbarError } = useSnackbar();
  const caseReducer = useSelector((state) => {
    return state.caseReducer;
  });
  /**
   * Initialize form inputs from `opponentData` when popup opens/props change.
   * Why: keeps local form state in sync with latest opponent info.
   * When: runs whenever `opponentData` changes.
   * Risks: overwrites unsaved edits if parent re-renders with new object.
   * Fix: guard with `props.open`.
   */
  useEffect(() => {
    if (!props.open) return;
    setInputs({
      first_name: (opponentData && opponentData.first_name) || "",
      last_name: (opponentData && opponentData.last_name) || "",
      company: (opponentData && opponentData.company) || "",
      gender: (opponentData && opponentData.gender) || "",
      title: (opponentData && opponentData.title) || "",
      email: (opponentData && opponentData.email) || "",
      phone_number: (opponentData && opponentData.phone_number) || "",
      address_line_1: (opponentData && opponentData.address_line_1) || "",
      address_line_2: (opponentData && opponentData.address_line_2) || "",
      postal_code: (opponentData && opponentData.postal_code) || "",
      city: (opponentData && opponentData.city) || "",
      country: (opponentData && opponentData.country) || "",
    });
  }, [opponentData, props.open]);

  function handleInputChange(e) {
    const { id, value, name } = e.target;
    setInputs((inputs) => ({ ...inputs, [id || name]: value }));
  }

  function actionClicked() {
    setIsClicked(true);

    var inputCopy = { ...inputs };

    if (isNull(inputCopy.email)) {
      showSnackbarError("Email is required.");
      return;
    } else {
      inputCopy.email = inputs.email.trim();
      if (!validateEmail(inputCopy.email)) {
        showSnackbarError("Invalid email format.");
        return;
      }
    }
    if (!inputs.gender) {
      inputCopy.gender = "NOT_KNOWN";
    }
    dispatch(caseActions.updateCaseOpponentDetails(props.caseId, inputCopy));
  }

  /**
   * Handle opponent details update result.
   * Why: refresh case data and close popup after successful update.
   * When: runs on `CASE_UPDATE_OPPONENT_DETAILS` status change.
   * Also listened by other case views via `caseReducer` refreshes.
   * Risks: missing error handling path; status not cleared on failure.
   * Fix: add error snackbar + clear status on failure.
   */
  useEffect(() => {
    const responseStatus = caseReducer.status.CASE_UPDATE_OPPONENT_DETAILS;
    if (responseStatus && responseStatus === "success") {
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_OPPONENT_DETAILS"));
      props.close();
    } else if (catchError(responseStatus)) {
      showSnackbarError(responseStatus);
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_OPPONENT_DETAILS"));
    }
    // eslint-disable-next-line
  }, [dispatch, caseReducer.status.CASE_UPDATE_OPPONENT_DETAILS]);

  const handleCancel = () => {
    props.close();
    setInputs({
      first_name: (opponentData && opponentData.first_name) || "",
      last_name: (opponentData && opponentData.last_name) || "",
      company: (opponentData && opponentData.company) || "",
      gender: (opponentData && opponentData.gender) || "",
      title: (opponentData && opponentData.title) || "",
      email: (opponentData && opponentData.email) || "",
      phone_number: (opponentData && opponentData.phone_number) || "",
      address_line_1: (opponentData && opponentData.address_line_1) || "",
      address_line_2: (opponentData && opponentData.address_line_2) || "",
      postal_code: (opponentData && opponentData.postal_code) || "",
      city: (opponentData && opponentData.city) || "",
      country: (opponentData && opponentData.country) || "",
    });
  };

  return (
    <DialogPopup
      title="Website Owner Contact Info"
      open={props.open}
      onClose={() => handleCancel()}
    >
      <AppSnackbar {...snackbarProps} />
      <div className={styles.content_container}>
        <div className={styles.contact_container}>
          <TextfieldInput
            label="Email"
            id="email"
            value={inputs.email}
            onChange={handleInputChange}
            disabled={inputDisabled}
            error={
              isClicked &&
              (isNull(inputs.email) || !validateEmail(inputs.email))
            }
            required
          />

          <div className={styles.TwoInputs}>
            <TextfieldInput
              label="Gender"
              select
              id="gender"
              name="gender"
              className={styles.subInput}
              value={inputs.gender}
              onChange={handleInputChange}
              disabled={inputDisabled}
            >
              {Genders.map((gender) => (
                <MenuItem key={gender.id} value={gender.id}>
                  {`${gender.label} `}
                </MenuItem>
              ))}
            </TextfieldInput>
            <TextfieldInput
              label="Title"
              id="title"
              className={styles.subInput}
              value={inputs.title}
              onChange={handleInputChange}
              disabled={inputDisabled}
            />
          </div>

          <TextfieldInput
            label="First Name"
            id="first_name"
            value={inputs.first_name}
            onChange={handleInputChange}
            disabled={inputDisabled}
          />
          <TextfieldInput
            label="Last Name"
            id="last_name"
            value={inputs.last_name}
            onChange={handleInputChange}
            disabled={inputDisabled}
          />

          <TextfieldInput
            label="Phone"
            id="phone_number"
            value={inputs.phone_number}
            onChange={handleInputChange}
            disabled={inputDisabled}
          />
        </div>

        <div className={styles.contact_container}>
          <TextfieldInput
            label="Company Name"
            id="company"
            value={inputs.company}
            onChange={handleInputChange}
            disabled={inputDisabled}
          />
          <TextfieldInput
            label="Address Line 1"
            id="address_line_1"
            value={inputs.address_line_1}
            onChange={handleInputChange}
            disabled={inputDisabled}
          />
          <TextfieldInput
            label="Address Line 2"
            id="address_line_2"
            value={inputs.address_line_2}
            onChange={handleInputChange}
            disabled={inputDisabled}
          />
          <TextfieldInput
            label="City"
            id="city"
            value={inputs.city}
            onChange={handleInputChange}
            disabled={inputDisabled}
          />

          <div className={styles.TwoInputs}>
            <TextfieldInput
              label="Postal Code"
              id="postal_code"
              className={styles.subInput}
              value={inputs.postal_code}
              onChange={handleInputChange}
              disabled={inputDisabled}
            />
            <TextfieldInput
              className={`${styles.subInput} ${styles.countrySelect}`}
              select
              label="Country"
              id="country"
              name="country"
              value={inputs.country}
              onChange={handleInputChange}
              disabled={inputDisabled}
            >
              <MenuItem value={""}>Not Set</MenuItem>
              {CountryAutocompleteList.map((country) => (
                <MenuItem key={country.id} value={country.id}>
                  {`${country.label} (${country.id})`}
                </MenuItem>
              ))}
            </TextfieldInput>
          </div>
        </div>
      </div>
      <div className="action_container">
        {!inputDisabled ? (
          <>
            <Button
              className="btn_cancel"
              data-testid="contact-info-cancel-btn"
              onClick={() => handleCancel()}
            >
              Cancel
            </Button>
            <Button
              className="btn_confirm"
              data-testid="contact-info-confirm-btn"
              onClick={actionClicked}
              disabled={inputDisabled}
            >
              Confirm
            </Button>
          </>
        ) : (
          <Button
            className="btn_cancel"
            data-testid="contact-info-close-btn"
            onClick={() => props.close()}
          >
            Close
          </Button>
        )}
      </div>
    </DialogPopup>
  );
}

export default ContactInfoPopup;
